/**
 * Shop content filter (mirrors Core ContentModeration).
 * EN / NL / FR — phrases, whole words, compound stems, and glued combinations (poeplul).
 */

export const INAPPROPRIATE_FIELD_MESSAGE =
    'This text contains language that is not allowed. Please rephrase.';

export const INAPPROPRIATE_FIELD_KEY = 'form.productform.errors.inappropriate';

const BLOCKED_PHRASES = [
    'ta mere', 'nique ta mere', 'va te faire', 'va te faire foutre', 'fils de pute',
    'sale pute', 'gros con', 'encule toi', 'ferme ta gueule', 'casse toi',
    'mother fucker', 'son of a bitch', 'piece of shit', 'go fuck yourself',
    'what the fuck', 'shut the fuck',
    'kanker lijer', 'kankerlijer', 'tyfus lijer', 'tering lijer',
    'krijg de kanker', 'krijg de tyfus', 'krijg de tering',
    'rot op', 'flikker op', 'sodemieter op',
];

/** Short roots: exact match + building blocks for glued combos (not free substring alone). */
const GLUE_PIECES = [
    // EN — no "god" (false-positive: "good"→"god" when doubles collapsed; NL godver* is in stems)
    'ass', 'sex', 'cum', 'tit', 'fag', 'cock', 'dick', 'cunt', 'shit', 'fuck',
    'porn', 'anal', 'anus', 'rape', 'slut', 'tits', 'boob',
    // NL
    'lul', 'kut', 'pik', 'kak', 'reet', 'kont', 'homo', 'poep', 'neuk', 'kloot',
    'slet', 'hoer', 'trut', 'sukkel', 'debiel', 'eikel', 'stront', 'scheet',
    'piemel', 'klote', 'tering', 'tyfus', 'kanker', 'flikker', 'neger',
    'aars', 'bal', 'tepels', 'tepel',
    // FR
    'cul', 'con', 'bite', 'pute', 'pd', 'sexe', 'foutre', 'baise', 'nique', 'merde',
    'chatte', 'couille', 'nichon', 'zizi', 'teub', 'gland', 'burne', 'salope',
    'encule', 'connard', 'batard',
];

const COMPOUND_STEMS = [
    // EN
    'fuck', 'fucker', 'fucking', 'motherfucker', 'shit', 'bullshit', 'asshole', 'arsehole',
    'bitch', 'bastard', 'cunt', 'cock', 'dick', 'dickhead', 'pussy', 'vagina', 'penis',
    'whore', 'slut', 'porn', 'porno', 'hentai', 'onlyfans',
    'rape', 'rapist', 'molest', 'pedophile', 'paedophile', 'pedo', 'paedo',
    'nigger', 'nigga', 'faggot', 'retard', 'retarded', 'tranny',
    'blowjob', 'handjob', 'cumshot', 'deepthroat', 'dildo', 'vibrator',
    'scrotum', 'testicle', 'clitoris', 'clit', 'anus', 'anal',
    'boobs', 'tits', 'titties', 'labia', 'semen', 'ejaculat', 'ejaculation',
    'butthole', 'rimjob', 'facesit', 'squirting', 'nazi', 'hitler',
    // NL
    'klootzak', 'kloot', 'klote', 'lullo', 'lulletje', 'piemel', 'piemeltje',
    'kutwijf', 'kutting', 'kutje', 'kuthol', 'kutgat', 'kutten',
    'hoeren', 'hoertje', 'sletten', 'sletje',
    'neuken', 'neuker', 'neukert', 'geneukt',
    'godver', 'godverdomme', 'godverdekke',
    'kanker', 'kankeren', 'kankerzooi', 'kankerlijer', 'kankerkop',
    'tering', 'teringlijer', 'tyfus', 'tyfuslijer', 'tyfuszooi',
    'mongool', 'mongolen', 'mongooltje',
    'flikker', 'flikkers', 'flikkertje',
    'neger', 'negers', 'negertje',
    'trutten', 'rotzak', 'zakkenwasser',
    'poepen', 'poepgat', 'poepgaatje', 'poepgatje', 'poepje', 'poeplul', 'lulpoep',
    'stront', 'kaka', 'kontgat', 'kontgaatje', 'kontje',
    'reetgat', 'reetgaatje', 'reetridder', 'reetje',
    'schaamlip', 'schaamlippen', 'tepel', 'tepels',
    'balzak', 'balzakken', 'eikels',
    'aars', 'aarsgat', 'aarsgaatje',
    'vagina', 'penis', 'clitoris', 'scrotum',
    'lamzak', 'idioot', 'debiel',
    'gaatje', 'kuthol', 'lulhond', 'kutlul', 'lulkut',
    // FR
    'putain', 'salope', 'salopes', 'connard', 'connards', 'connasse',
    'encule', 'enculer', 'enculee', 'foutre',
    'couille', 'couilles', 'chatte', 'chattes',
    'niquer', 'branler', 'branlette', 'branleur',
    'conne', 'pede', 'pederaste', 'tapette', 'gouine',
    'trouduc', 'trouducu', 'enfoire', 'bordel',
    'fesses', 'chier', 'chiant', 'chiante', 'emmerde', 'emmerdeur', 'emmerder',
    'batard', 'salopard', 'ordure', 'salaud',
    'enculade', 'suceuse', 'suceur', 'pipeuse',
    'niqueur', 'baiseur',
    'merdeux', 'merdeuse',
    'pedo', 'pedophile', 'pedophilie',
    'bites', 'zizi', 'zboub', 'teub',
    'minou', 'nichon', 'nichons', 'teeton', 'teetons',
    'chibre', 'gland', 'burne', 'burnes',
];

const NL_SUFFIXES = ['tjes', 'pjes', 'jes', 'tje', 'pje', 'je', 'kes', 'ke', 'en', 's'];

const GLUE_SET = new Set(GLUE_PIECES.map((w) => normalizeToken(w)).filter((w) => w.length >= 2));
const STEM_LIST = [
    ...new Set(COMPOUND_STEMS.map((s) => normalizeToken(s)).filter((s) => s.length >= 3)),
].sort((a, b) => b.length - a.length);
const PARTITION_PIECES = [...new Set([...GLUE_SET, ...STEM_LIST])].sort((a, b) => b.length - a.length);
const PHRASE_SET = BLOCKED_PHRASES.map((p) => normalizeForMatch(p)).filter((p) => p.length >= 3);

function stripDiacritics(input: string): string {
    return input.normalize('NFD').replace(/\p{M}/gu, '');
}

export function normalizeForMatch(input: string): string {
    if (!input) return '';
    let s = stripDiacritics(input).toLowerCase();
    s = s
        .replace(/@/g, 'a')
        .replace(/\$/g, 's')
        .replace(/0/g, 'o')
        .replace(/1/g, 'i')
        .replace(/3/g, 'e')
        .replace(/4/g, 'a')
        .replace(/5/g, 's')
        .replace(/7/g, 't')
        .replace(/!/g, 'i');
    s = s.replace(/[^\p{L}]+/gu, ' ').replace(/\s+/g, ' ').trim();
    return s;
}

function normalizeToken(token: string): string {
    return normalizeForMatch(token).replace(/\s+/g, '');
}

/** Collapse spam runs of 3+ same letters only (fuuuck→fuck). Keep normal doubles (good≠god). */
function collapseRepeats(token: string): string {
    if (token.length < 3) return token;
    let out = '';
    let i = 0;
    while (i < token.length) {
        const c = token[i];
        let j = i + 1;
        while (j < token.length && token[j] === c) j++;
        const run = j - i;
        if (run >= 3) out += c;
        else out += token.slice(i, j);
        i = j;
    }
    return out;
}

function stripSuffixes(token: string): string[] {
    const out: string[] = [];
    let current = token;
    for (let round = 0; round < 2; round++) {
        let stripped = false;
        for (const suf of NL_SUFFIXES) {
            if (current.length > suf.length + 2 && current.endsWith(suf)) {
                current = current.slice(0, -suf.length);
                out.push(current);
                stripped = true;
                break;
            }
        }
        if (!stripped) break;
    }
    return out;
}

function tokenVariants(token: string): string[] {
    const set = new Set<string>();
    set.add(token);
    const collapsed = collapseRepeats(token);
    set.add(collapsed);
    for (const base of [token, collapsed]) {
        for (const s of stripSuffixes(base)) {
            if (s.length >= 2) set.add(s);
        }
    }
    return [...set];
}

function stemMatches(token: string): boolean {
    if (token.length < 3) return false;
    for (const stem of STEM_LIST) {
        if (token.length >= stem.length && token.includes(stem)) return true;
    }
    return false;
}

/** Full cover by ≥2 blocked pieces → glued combo (poeplul). Not lullaby. */
function canPartitionIntoBlockedPieces(token: string): boolean {
    const n = token.length;
    const dp = new Array<boolean>(n + 1).fill(false);
    const pieceCount = new Array<number>(n + 1).fill(0);
    dp[0] = true;

    for (let i = 0; i < n; i++) {
        if (!dp[i]) continue;
        for (const piece of PARTITION_PIECES) {
            const len = piece.length;
            if (len < 2 || i + len > n) continue;
            if (token.slice(i, i + len) !== piece) continue;
            if (!dp[i + len]) {
                dp[i + len] = true;
                pieceCount[i + len] = pieceCount[i] + 1;
            } else {
                pieceCount[i + len] = Math.max(pieceCount[i + len], pieceCount[i] + 1);
            }
        }
    }
    return dp[n] && pieceCount[n] >= 2;
}

function isBlockedToken(token: string): boolean {
    if (token.length < 2) return false;
    if (GLUE_SET.has(token)) return true;
    if (stemMatches(token)) return true;
    if (token.length >= 4 && canPartitionIntoBlockedPieces(token)) return true;
    return false;
}

export function containsBlockedContent(text: string | null | undefined): boolean {
    if (!text?.trim()) return false;
    const normalized = normalizeForMatch(text);
    if (!normalized) return false;

    for (const phrase of PHRASE_SET) {
        if (normalized.includes(phrase)) return true;
    }

    const compact = normalized.replace(/\s+/g, '');
    for (const token of normalized.split(' ').filter(Boolean)) {
        for (const v of tokenVariants(token)) {
            if (isBlockedToken(v)) return true;
        }
    }
    if (isBlockedToken(compact)) return true;
    return false;
}

export function getInappropriateMessage(asKey = true): string {
    return asKey ? INAPPROPRIATE_FIELD_KEY : INAPPROPRIATE_FIELD_MESSAGE;
}
